/**
 * Switches Add to Cart → Request Quote when Avis custom options are selected:
 * - Size: Custom Size / Custom Print, Custom Sizing
 * - Shape: Custom Shape
 */
(function () {
  const QUOTE_OPTION_RULES = [
    {
      names: ['Size'],
      values: new Set(['Custom Size / Custom Print', 'Custom Sizing']),
    },
    {
      names: ['Shape'],
      values: new Set(['Custom Shape']),
    },
  ];
  const QUOTE_OPTION_NAMES = QUOTE_OPTION_RULES.flatMap((rule) => rule.names);
  const QUOTE_OPTION_INPUT_SELECTOR = QUOTE_OPTION_NAMES.map(
    (name) =>
      `input[type="radio"][name="${name}"], input[type="radio"][field-name="${name}"], select[name="${name}"], select[field-name="${name}"]`
  ).join(', ');
  const ATC_BUTTON_SELECTOR =
    'product-form-component button[name="add"], .sticky-add-to-cart__button';
  const VARIANT_UPDATE_EVENT = 'variant:update';
  const SECTION_LOAD_EVENT = 'shopify:section:load';

  class ByteAvisQuoteCta {
    constructor(configElement) {
      this.configElement = configElement;
      this.quoteUrl = configElement.dataset.quoteUrl || '/pages/get-a-quote';
      this.quoteLabel = configElement.dataset.quoteLabel || 'Request Quote';
      this.isQuoteMode = false;
      this.isSyncing = false;
      this.observer = null;
      this.syncTimer = null;

      this.boundHandleOptionInteraction = this.handleOptionInteraction.bind(this);
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleSubmit = this.handleSubmit.bind(this);
      this.boundHandleVariantUpdate = this.handleVariantUpdate.bind(this);
      this.boundSync = this.sync.bind(this);
    }

    init() {
      this.#bindEvents();
      this.#observeAvisOptions();
      this.sync();
    }

    destroy() {
      this.#unbindEvents();

      if (this.syncTimer) {
        clearTimeout(this.syncTimer);
        this.syncTimer = null;
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    handleOptionInteraction(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const isQuoteOptionInput = target.matches(QUOTE_OPTION_INPUT_SELECTOR);
      const isAvisOptionChoice = Boolean(target.closest('.avp-productoptioncheckwrapper, .avp-option'));

      if (!isQuoteOptionInput && !isAvisOptionChoice) return;

      requestAnimationFrame(() => this.sync());
    }

    handleClick(event) {
      if (!this.isQuoteMode) return;

      const button = event.target.closest(ATC_BUTTON_SELECTOR);
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      window.location.assign(this.quoteUrl);
    }

    handleSubmit(event) {
      if (!this.isQuoteMode) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[data-type="add-to-cart-form"]')) return;

      event.preventDefault();
      event.stopPropagation();
      window.location.assign(this.quoteUrl);
    }

    handleVariantUpdate() {
      // Theme morph replaces ATC markup after variant updates.
      requestAnimationFrame(() => this.sync());
    }

    sync() {
      if (this.isSyncing) return;

      this.isSyncing = true;

      try {
        const shouldShowRequestQuote = this.#shouldShowRequestQuote();
        this.isQuoteMode = shouldShowRequestQuote;

        const buttons = document.querySelectorAll(ATC_BUTTON_SELECTOR);

        for (const button of buttons) {
          if (shouldShowRequestQuote) {
            this.#enableQuoteMode(button);
          } else {
            this.#disableQuoteMode(button);
          }
        }
      } finally {
        this.isSyncing = false;
      }
    }

    #shouldShowRequestQuote() {
      for (const rule of QUOTE_OPTION_RULES) {
        for (const optionName of rule.names) {
          const selectedValue = this.#getSelectedOptionValue(optionName);
          if (selectedValue && rule.values.has(selectedValue)) {
            return true;
          }
        }
      }

      return false;
    }

    #getSelectedOptionValue(optionName) {
      const checkedRadio = document.querySelector(
        `input[type="radio"][name="${optionName}"]:checked, input[type="radio"][field-name="${optionName}"]:checked`
      );

      if (checkedRadio instanceof HTMLInputElement) {
        return checkedRadio.value.trim();
      }

      const optionSelect = document.querySelector(
        `select[name="${optionName}"], select[field-name="${optionName}"]`
      );
      if (optionSelect instanceof HTMLSelectElement) {
        return optionSelect.value.trim();
      }

      return '';
    }

    #enableQuoteMode(button) {
      if (!(button instanceof HTMLButtonElement)) return;

      if (!button.dataset.byteOriginalLabel) {
        button.dataset.byteOriginalLabel = this.#getPrimaryLabelText(button);
      }

      if (button.getAttribute('type') === 'submit' || button.type === 'submit') {
        button.dataset.byteOriginalType = 'submit';
        button.type = 'button';
      }

      button.dataset.byteQuoteCta = 'true';
      button.classList.add('is-request-quote');
      button.removeAttribute('disabled');
      this.#setButtonLabel(button, this.quoteLabel);
    }

    #disableQuoteMode(button) {
      if (!(button instanceof HTMLButtonElement)) return;
      if (!button.dataset.byteQuoteCta && !button.classList.contains('is-request-quote')) return;

      const originalLabel = button.dataset.byteOriginalLabel;
      if (originalLabel) {
        this.#setButtonLabel(button, originalLabel);
      }

      if (button.dataset.byteOriginalType === 'submit') {
        button.type = 'submit';
        delete button.dataset.byteOriginalType;
      }

      button.classList.remove('is-request-quote');
      delete button.dataset.byteQuoteCta;
      delete button.dataset.byteOriginalLabel;
    }

    #getPrimaryLabelText(button) {
      const nestedLabel = button.querySelector('.add-to-cart-text__content span span');
      if (nestedLabel && !nestedLabel.hasAttribute('ref')) {
        return nestedLabel.textContent?.trim() || '';
      }

      const content = button.querySelector('.add-to-cart-text__content > span');
      if (content) {
        const text = Array.from(content.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent || '')
          .join('')
          .trim();

        if (text) return text;
        return content.textContent?.trim() || '';
      }

      return button.textContent?.trim() || '';
    }

    #setButtonLabel(button, label) {
      const nestedLabel = button.querySelector('.add-to-cart-text__content span span');
      if (nestedLabel && !nestedLabel.hasAttribute('ref')) {
        nestedLabel.textContent = label;
        return;
      }

      const contentSpan = button.querySelector('.add-to-cart-text__content > span');
      if (contentSpan) {
        const quantityDisplay = contentSpan.querySelector('[ref="quantityDisplay"]');

        for (const node of [...contentSpan.childNodes]) {
          if (node === quantityDisplay) continue;
          node.remove();
        }

        contentSpan.insertBefore(document.createTextNode(label), contentSpan.firstChild);

        if (quantityDisplay) {
          quantityDisplay.style.display = 'none';
        }

        return;
      }

      button.textContent = label;
    }

    #bindEvents() {
      document.addEventListener('change', this.boundHandleOptionInteraction, true);
      document.addEventListener('click', this.boundHandleOptionInteraction, true);
      document.addEventListener('click', this.boundHandleClick, true);
      document.addEventListener('submit', this.boundHandleSubmit, true);
      document.addEventListener(VARIANT_UPDATE_EVENT, this.boundHandleVariantUpdate);
    }

    #unbindEvents() {
      document.removeEventListener('change', this.boundHandleOptionInteraction, true);
      document.removeEventListener('click', this.boundHandleOptionInteraction, true);
      document.removeEventListener('click', this.boundHandleClick, true);
      document.removeEventListener('submit', this.boundHandleSubmit, true);
      document.removeEventListener(VARIANT_UPDATE_EVENT, this.boundHandleVariantUpdate);
    }

    #observeAvisOptions() {
      this.observer = new MutationObserver((mutations) => {
        if (this.isSyncing) return;

        const shouldSync = mutations.some((mutation) => {
          if (mutation.type === 'attributes') {
            const target = mutation.target;
            return target instanceof Element && target.matches(QUOTE_OPTION_INPUT_SELECTOR);
          }

          for (const node of mutation.addedNodes) {
            if (!(node instanceof Element)) continue;
            if (node.matches(QUOTE_OPTION_INPUT_SELECTOR) || node.matches('.avp-option')) return true;
            if (node.querySelector?.(QUOTE_OPTION_INPUT_SELECTOR)) return true;
          }

          return false;
        });

        if (!shouldSync) return;

        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.sync(), 50);
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked'],
      });
    }
  }

  let instance = null;

  function boot() {
    const configElement = document.querySelector('[data-byte-avis-quote-cta]');
    if (!configElement) return;

    if (instance) {
      instance.destroy();
      instance = null;
    }

    instance = new ByteAvisQuoteCta(configElement);
    instance.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener(SECTION_LOAD_EVENT, boot);
})();
